import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import api from "../../lib/api";

export function CategorySEO({ currentCat, products, total, slug, searchQ }) {
    const pageTitle = currentCat?.name || (searchQ ? `Hledání: ${searchQ}` : "Katalog produktů");
    const pageDesc = currentCat ? `${currentCat.name} - ${total} produktů. Solar-Green.cz` : "Kompletní katalog solárních produktů - Solar-Green.cz";

    const collectionLd = useMemo(() => {
        if (!currentCat || !products.length) return null;
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": currentCat.name,
            "description": currentCat.description || pageDesc,
            "url": `https://solar-green.cz/kategorie/${slug}`,
            "numberOfItems": total,
            "hasPart": products.slice(0, 20).map(p => ({
                "@type": "Product",
                "name": p.name,
                "url": `https://solar-green.cz/produkt/${p.slug}`,
                "image": p.main_image || undefined,
                "offers": {
                    "@type": "Offer",
                    "price": String(p.sale_price && p.sale_price < p.price ? p.sale_price : p.price),
                    "priceCurrency": "CZK",
                    "availability": p.stock_status === "in_stock" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                }
            }))
        });
    }, [currentCat, products, total, slug, pageDesc]);

    const breadcrumbLd = useMemo(() => {
        if (!currentCat) return null;
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Solar-Green.cz", "item": "https://solar-green.cz" },
                { "@type": "ListItem", "position": 2, "name": currentCat.name }
            ]
        });
    }, [currentCat]);

    const [faqSchemaLd, setFaqSchemaLd] = useState(null);
    useEffect(() => {
        if (!slug) { setFaqSchemaLd(null); return; }
        api.get(`/seo/faq-schema/category/${slug}`)
            .then(r => { if (r.data?.schema) setFaqSchemaLd(JSON.stringify(r.data.schema)); else setFaqSchemaLd(null); })
            .catch(() => setFaqSchemaLd(null));
    }, [slug]);

    return (
        <Helmet>
            <title>{pageTitle ? `${pageTitle} | Solar-Green.cz` : "Katalog | Solar-Green.cz"}</title>
            <meta name="description" content={pageDesc} />
            <meta property="og:title" content={`${pageTitle} | Solar-Green.cz`} />
            <meta property="og:description" content={pageDesc} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`https://solar-green.cz/kategorie${slug ? `/${slug}` : ""}`} />
            {currentCat?.image && <meta property="og:image" content={currentCat.image} />}
            <link rel="canonical" href={`https://solar-green.cz/kategorie${slug ? `/${slug}` : ""}`} />
            {collectionLd && <script type="application/ld+json">{collectionLd}</script>}
            {breadcrumbLd && <script type="application/ld+json">{breadcrumbLd}</script>}
            {faqSchemaLd && <script type="application/ld+json">{faqSchemaLd}</script>}
        </Helmet>
    );
}
